import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonService } from 'src/services/common/common.service';
import { SeoService } from 'src/services/seo/seo.service';
import { ModalDirective } from 'angular-bootstrap-md';

const YOUTUBE_EMBED_RE = /(?:youtube\.com\/(?:embed\/|watch\?(?:[^&]*&)*v=)|youtu\.be\/)([^?&]+)/;

@Component({
  selector: 'app-self-care',
  templateUrl: './self-care.component.html',
  styleUrls: ['./self-care.component.scss']
})
export class SelfCareComponent implements OnInit, AfterViewInit {

  selectedTool: any = {}
  selectedVideoUrl: SafeResourceUrl | undefined
  selectedTabIndex = 0
  selfCareList: any = [
    {
      resourceName: "crisis",
      title: "selfCare.tab1.title",
      tab: [{
        title: "selfCare.tab1.tools.tool1.name",
        content: "selfCare.tab1.tools.tool1.text",
        link: this.commonService.resource.selfCare.tab1.technique1.link,
        thumbnail: this.commonService.resource.selfCare.tab1.technique1.thumbnail
      }, {
        title: "selfCare.tab1.tools.tool2.name",
        content: "selfCare.tab1.tools.tool2.text",
        link: this.commonService.resource.selfCare.tab1.technique2.link,
        thumbnail: this.commonService.resource.selfCare.tab1.technique2.thumbnail
      }, {
        title: "selfCare.tab1.tools.tool3.name",
        content: "selfCare.tab1.tools.tool3.text",
        link: this.commonService.resource.selfCare.tab1.technique3.link,
        thumbnail: this.commonService.resource.selfCare.tab1.technique3.thumbnail
      }, {
        title: "selfCare.tab1.tools.tool4.name",
        content: "selfCare.tab1.tools.tool4.text",
        link: this.commonService.resource.selfCare.tab1.technique4.link,
        thumbnail: this.commonService.resource.selfCare.tab1.technique4.thumbnail
      }]
    }, {
      resourceName: "movement",
      title: "selfCare.tab2.title",
      tab: [{
        title: "selfCare.tab2.tools.tool1.name",
        content: "selfCare.tab2.tools.tool1.text",
        link: this.commonService.resource.selfCare.tab2.technique1.link,
        thumbnail: this.commonService.resource.selfCare.tab2.technique1.thumbnail
      }, {
        title: "selfCare.tab2.tools.tool2.name",
        content: "selfCare.tab2.tools.tool2.text",
        link: this.commonService.resource.selfCare.tab2.technique2.link,
        thumbnail: this.commonService.resource.selfCare.tab2.technique2.thumbnail
      }, {
        title: "selfCare.tab2.tools.tool3.name",
        content: "selfCare.tab2.tools.tool3.text",
        link: this.commonService.resource.selfCare.tab2.technique3.link,
        thumbnail: this.commonService.resource.selfCare.tab2.technique3.thumbnail
      }]
    },
    {
      resourceName: "journaling",
      title: "selfCare.tab3.title",
      tab: [{
        title: "selfCare.tab3.questions.question1.name",
        content: "selfCare.tab3.questions.question1.question",
        subContent: "selfCare.tab3.questions.question1.answer",
        link: "",
        thumbnail: this.commonService.resource.selfCare.tab3.thumbnail
      }, {
        title: "selfCare.tab3.questions.question2.name",
        content: "selfCare.tab3.questions.question2.question",
        subContent: "selfCare.tab3.questions.question2.answer",
        link: "",
        thumbnail: this.commonService.resource.selfCare.tab3.thumbnail
      }, {
        title: "selfCare.tab3.questions.question3.name",
        content: "selfCare.tab3.questions.question3.question",
        subContent: "selfCare.tab3.questions.question3.answer",
        link: "",
        thumbnail: this.commonService.resource.selfCare.tab3.thumbnail
      }, {
        title: "selfCare.tab3.questions.question4.name",
        content: "selfCare.tab3.questions.question4.question",
        subContent: "selfCare.tab3.questions.question4.answer",
        link: "",
        thumbnail: this.commonService.resource.selfCare.tab3.thumbnail
      }, {
        title: "selfCare.tab3.questions.question5.name",
        content: "selfCare.tab3.questions.question5.question",
        subContent: "selfCare.tab3.questions.question5.answer",
        link: "",
        thumbnail: this.commonService.resource.selfCare.tab3.thumbnail
      }]
    }
  ]

  @ViewChild('selfCareModal') showModal: ModalDirective | undefined;

  constructor(
    private _sanitizer: DomSanitizer,
    public commonService: CommonService,
    private seoService: SeoService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.seoService.setMetaTags(
      'Emotional Self Care Tips, Self Care for Emotional Distress',
      'Find practical emotional self care tips to help you manage stress, cope with emotional distress, and take care of your mental wellbeing every day.'
    );
  }

  ngAfterViewInit(): void {
    // Deferred a tick: the @ViewChild modal needs the view ready, but
    // mutating our own bound state (selectedIndex, modal) synchronously
    // in ngAfterViewInit trips Angular's ExpressionChangedAfterItHasBeenCheckedError.
    setTimeout(() => this.openFromQueryParams());
  }

  /**
   * Keeps the selected tab in the URL so it survives a page reload.
   * Skips navigating when the index change merely reflects the URL
   * we just synced from (see openFromQueryParams), so a fresh load
   * doesn't immediately overwrite its own `option` param.
   */
  onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
    const tab = this.selfCareList[index];
    if (!tab) return;

    if (this.route.snapshot.queryParamMap.get('type') === tab.resourceName) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { type: tab.resourceName, option: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Shared resource links look like /self-care?type=journaling&option=3 —
   * `type` matches a tab's resourceName and `option` is the tab's
   * 1-based position within that type. Selects the matching tab and,
   * when an option is given, opens its detail modal directly.
   */
  private openFromQueryParams(): void {
    const type = this.route.snapshot.queryParamMap.get('type');
    if (!type) return;

    const tabIndex = this.selfCareList.findIndex(
      (t: any) => t.resourceName?.toLowerCase() === type.toLowerCase()
    );
    if (tabIndex === -1) return;

    this.selectedTabIndex = tabIndex;

    const option = this.route.snapshot.queryParamMap.get('option');
    if (option) {
      const tool = this.selfCareList[tabIndex].tab[Number(option) - 1];
      if (tool) {
        this.showDetails(tool);
      }
    }
  }

  /**
   * Shows details of selected self care
   * @param tool 
   */
  showDetails(tool: any){
    this.selectedTool = tool
    this.selectedVideoUrl = tool.link
      ? this._sanitizer.bypassSecurityTrustResourceUrl(this.toEmbedUrl(tool.link)+"?modestbranding=1&showinfo=0")
      : undefined
    this.showModal?.show()
  }

  isYoutubeLink(link: string): boolean {
    return !!link && YOUTUBE_EMBED_RE.test(link);
  }

  private extractYoutubeId(link: string): string | undefined {
    return link.match(YOUTUBE_EMBED_RE)?.[1];
  }

  /**
   * Admin-entered links may be regular youtube.com/watch or youtu.be links,
   * which YouTube refuses to render inside an iframe. Normalize to the
   * /embed/ form the player actually allows framing.
   */
  toEmbedUrl(link: string): string {
    const videoId = this.extractYoutubeId(link);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : link;
  }
}
